import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { GstRegistrationModal, useGstEnabled } from './GstRegistration';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../utils/permissionUtils';
import HistoryButton from './HistoryButton';

import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';
export const PURCHASE_NOTE_REASONS = {
  PURCHASE_RETURN: 'Goods returned',
  DISCOUNT_RECEIVED: 'Discount received',
  QUALITY_ISSUE: 'Quality issue',
  CORRECTION_IN_INVOICE: 'Correction in invoice',
  OTHERS: 'Others'
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};

/**
 * Raises a debit note on the vendor against a recorded purchase invoice: goods sent back or a discount
 * received. It takes the value and GST off what you owe and gives back the input credit on that part. The
 * server works out the exact amounts; the figures here are an estimate.
 */
export function PurchaseNoteModal({ invoice, currency = '', onHide, onIssued }) {
  const [full, setFull] = useState(null);
  const [prior, setPrior] = useState([]);
  const [lots, setLots] = useState({});
  const [entries, setEntries] = useState({});
  const [reason, setReason] = useState('PURCHASE_RETURN');
  const [noteDate, setNoteDate] = useState('');
  const [vendorNoteNumber, setVendorNoteNumber] = useState('');
  const [vendorNoteDate, setVendorNoteDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!invoice?.id) return;
    let cancelled = false;
    setLoading(true);
    setEntries({}); setLots({}); setReason('PURCHASE_RETURN'); setNoteDate('');
    setVendorNoteNumber(''); setVendorNoteDate(''); setRemarks('');
    Promise.all([axios.get(`/api/purchase-invoices/${invoice.id}`), axios.get(`/api/purchase-notes/invoice/${invoice.id}`)])
      .then(([invRes, notesRes]) => {
        if (cancelled) return;
        setFull(invRes.data);
        setPrior(Array.isArray(notesRes.data) ? notesRes.data : []);
      })
      .catch(() => { if (!cancelled) toast.error('Could not load the invoice'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [invoice?.id]);

  const returned = useMemo(() => {
    const byLine = {};
    prior.filter((n) => n.status === 'ISSUED').forEach((n) => {
      (n.lines || []).forEach((l) => {
        const cur = byLine[l.purchaseInvoiceLineId] || { quantity: 0, taxable: 0 };
        cur.quantity += num(l.quantity);
        cur.taxable += num(l.taxableAmount);
        byLine[l.purchaseInvoiceLineId] = cur;
      });
    });
    return byLine;
  }, [prior]);

  const lines = full?.lines || [];

  const setEntry = (id, patch) => setEntries((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  // Offer lots to take stock out of once a quantity is entered for a line that has an item
  const loadLots = (line) => {
    if (lots[line.id] !== undefined || !line.itemId) return;
    setLots((prev) => ({ ...prev, [line.id]: [] }));
    axios.get(`/api/purchase-notes/invoice-lines/${line.id}/lots`)
      .then((r) => setLots((prev) => ({ ...prev, [line.id]: Array.isArray(r.data) ? r.data : [] })))
      .catch(() => {});
  };

  const estimate = (line) => {
    const e = entries[line.id] || {};
    const qty = num(e.quantity);
    const amount = num(e.amount);
    let taxable = 0;
    if (amount > 0) taxable = amount;
    else if (qty > 0 && num(line.quantity) > 0) taxable = (qty * num(line.taxableAmount)) / num(line.quantity);
    taxable = round2(taxable);
    return { taxable, tax: round2((taxable * num(line.taxRatePercent)) / 100) };
  };
  const totals = lines.reduce((acc, l) => {
    const est = estimate(l);
    return { taxable: acc.taxable + est.taxable, tax: acc.tax + est.tax };
  }, { taxable: 0, tax: 0 });
  const claimGivenBack = lines.reduce((s, l) => (l.itcEligible ? s + estimate(l).tax : s), 0);

  const submit = async () => {
    const payloadLines = lines
      .map((l) => {
        const e = entries[l.id] || {};
        return {
          purchaseInvoiceLineId: l.id,
          quantity: num(e.quantity) > 0 ? num(e.quantity) : null,
          taxableAmount: num(e.amount) > 0 ? num(e.amount) : null,
          grnLineItemId: num(e.quantity) > 0 && e.lotId ? Number(e.lotId) : null
        };
      })
      .filter((l) => l.quantity || l.taxableAmount);
    if (payloadLines.length === 0) {
      toast.error('Enter a returned quantity or an amount on at least one line');
      return;
    }
    try {
      setSaving(true);
      const response = await axios.post('/api/purchase-notes', {
        purchaseInvoiceId: invoice.id,
        noteDate: noteDate || null,
        reason,
        notes: remarks,
        vendorNoteNumber,
        vendorNoteDate: vendorNoteDate || null,
        lines: payloadLines
      });
      toast.success(`Debit note ${response.data.noteNumber} issued`);
      onIssued && onIssued(response.data);
      onHide();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to issue the note'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={Boolean(invoice)} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Return note{invoice ? ` — ${invoice.vendorName}, invoice ${invoice.vendorInvoiceNumber}` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading || !full ? (
          <div className="d-flex justify-content-center"><Spinner animation="border" /></div>
        ) : (
          <>
            <Alert variant="info" className="py-2 small">
              A debit note takes the returned value and its GST off what you owe the vendor, and gives back the input credit you claimed on that part.
              If you name a lot, the goods also leave stock through a goods issue note; that can&apos;t be undone.
            </Alert>
            <Row className="g-3 mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Reason</Form.Label>
                  <Form.Select value={reason} onChange={(e) => setReason(e.target.value)}>
                    {Object.entries(PURCHASE_NOTE_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Note date (blank = today)</Form.Label>
                  <DateInput value={noteDate} min={full.bookingDate} onChange={(e) => setNoteDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Vendor&apos;s credit note no. (optional)</Form.Label>
                  <Form.Control value={vendorNoteNumber} maxLength={40} onChange={(e) => setVendorNoteNumber(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Vendor&apos;s credit note date</Form.Label>
                  <DateInput value={vendorNoteDate} onChange={(e) => setVendorNoteDate(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>

            <Table bordered size="sm" responsive>
              <thead>
                <tr>
                  <th>Line</th><th className="text-end">Invoiced qty</th><th className="text-end">Taxable</th><th className="text-end">GST %</th>
                  <th className="text-end">Already returned</th><th style={{ width: 110 }}>Return qty</th>
                  <th style={{ width: 150 }}>Amount (optional)</th><th style={{ minWidth: 190 }}>Take stock out of lot</th><th className="text-end">Est. total</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const done = returned[l.id] || { quantity: 0, taxable: 0 };
                  const e = entries[l.id] || {};
                  const est = estimate(l);
                  const lineLots = lots[l.id] || [];
                  return (
                    <tr key={l.id}>
                      <td>{l.description}{!l.itcEligible && <div className="small text-muted">no credit was claimed</div>}</td>
                      <td className="text-end">{l.quantity ?? '-'}</td>
                      <td className="text-end">{currency} {num(l.taxableAmount).toFixed(2)}</td>
                      <td className="text-end">{l.taxRatePercent}</td>
                      <td className="text-end">{done.quantity || done.taxable ? `${done.quantity || '-'} (${num(done.taxable).toFixed(2)})` : '-'}</td>
                      <td>
                        <Form.Control size="sm" type="number" min="0" step="any" value={e.quantity || ''}
                          onChange={(ev) => { setEntry(l.id, { quantity: ev.target.value }); if (num(ev.target.value) > 0) loadLots(l); }} />
                      </td>
                      <td>
                        <Form.Control size="sm" type="number" min="0" step="0.01" value={e.amount || ''}
                          placeholder={l.quantity ? 'auto from qty' : 'required with qty'} onChange={(ev) => setEntry(l.id, { amount: ev.target.value })} />
                      </td>
                      <td>
                        {num(e.quantity) > 0 && l.itemId ? (
                          <Form.Select size="sm" value={e.lotId || ''} onChange={(ev) => setEntry(l.id, { lotId: ev.target.value })}>
                            <option value="">Don&apos;t move stock</option>
                            {lineLots.map((lot) => (
                              <option key={lot.id} value={lot.id}>{`${lot.lotNumber || lot.id} · ${lot.grnNumber} · ${lot.available} left${lot.location ? ` · ${lot.location}` : ''}`}</option>
                            ))}
                          </Form.Select>
                        ) : <span className="text-muted small">{l.itemId ? 'enter a quantity' : 'no inventory item'}</span>}
                      </td>
                      <td className="text-end">{est.taxable > 0 ? `${currency} ${(est.taxable + est.tax).toFixed(2)}` : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Remarks (optional)</Form.Label>
                  <Form.Control value={remarks} maxLength={1000} onChange={(e) => setRemarks(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={6} className="text-end">
                <div>Estimated value: <strong>{currency} {round2(totals.taxable).toFixed(2)}</strong></div>
                <div>Estimated GST: <strong>{currency} {round2(totals.tax).toFixed(2)}</strong> (credit given back: {currency} {round2(claimGivenBack).toFixed(2)})</div>
                <div className="fs-5">Reduces what you owe by: <strong>{currency} {round2(totals.taxable + totals.tax).toFixed(2)}</strong></div>
                <div className="text-muted small">The exact amount is worked out when the note is issued.</div>
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="danger" onClick={submit} disabled={saving || loading || !full}>{saving ? 'Issuing...' : 'Issue debit note'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

/** Debit notes raised on vendors: list, view and cancel (only while no stock has gone out). */
export function PurchaseNotesList({ active, reloadKey, currency = '' }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [localKey, setLocalKey] = useState(0);
  const { user } = useAuth();
  const [ewayNote, setEwayNote] = useState(null);
  // Raising a return's e-way bill is for the purchase roles (Org Admin included)
  const canMoveReturns = hasAnyRole(user, ['ORG_ADMIN', 'PROC_OPR', 'PROC_MGR']);
  const gstEnabled = useGstEnabled(active && canMoveReturns);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    axios.get('/api/purchase-notes')
      .then((r) => { if (!cancelled) setNotes(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) toast.error('Error fetching return notes'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, reloadKey, localKey]);

  useEffect(() => { setCancelling(false); setReason(''); }, [viewing]);

  const money = (v) => `${currency} ${num(v).toFixed(2)}`;
  const cancel = async () => {
    try {
      await axios.post(`/api/purchase-notes/${viewing.id}/cancel`, { reason });
      toast.success('Note cancelled and its entries reversed');
      setViewing(null);
      setLocalKey((k) => k + 1);
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to cancel the note'));
    }
  };

  return (
    <div className="mt-4">
      <h5>Return notes (debit notes to vendors)</h5>
      {loading ? (
        <div className="d-flex justify-content-center"><Spinner animation="border" role="status" /></div>
      ) : (
        <Card>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Note</th><th>Date</th><th>Vendor</th><th>Invoice</th><th>Reason</th>
                  <th className="text-end">Value</th><th className="text-end">GST</th><th className="text-end">Total</th>
                  <th className="text-end">Credit given back</th><th>Stock</th><th>Status</th><th />
                </tr>
              </thead>
              <tbody>
                {notes.map((n) => (
                  <tr key={n.id}>
                    <td><strong>{n.noteNumber}</strong></td>
                    <td>{formatDate(n.noteDate)}</td>
                    <td>{n.vendorName}</td>
                    <td>{n.vendorInvoiceNumber}</td>
                    <td>{PURCHASE_NOTE_REASONS[n.reason] || n.reason}</td>
                    <td className="text-end">{money(n.taxableAmount)}</td>
                    <td className="text-end">{money(n.totalTaxAmount)}</td>
                    <td className="text-end">{money(n.totalAmount)}</td>
                    <td className="text-end">{money(n.itcTotal)}</td>
                    <td>{(n.ginNumbers || []).length ? `Out (${n.ginNumbers.join(', ')})` : '-'}</td>
                    <td><Badge bg={n.status === 'ISSUED' ? 'success' : 'secondary'}>{n.status === 'ISSUED' ? 'Issued' : 'Cancelled'}</Badge></td>
                    <td className="text-nowrap">
                      <Button size="sm" variant="outline-info" onClick={() => setViewing(n)}>View</Button>
                      {canMoveReturns && gstEnabled.ewayEnabled && n.status === 'ISSUED' && (n.ginNumbers || []).length > 0 && (
                        <Button size="sm" variant="outline-secondary" className="ms-1" title="E-way bill for the goods sent back" onClick={() => setEwayNote(n)}>e-Way</Button>
                      )}
                      <HistoryButton documentType="PurchaseNote" documentId={n.id} label={`Purchase return ${n.noteNumber}`} className="ms-1" />
                    </td>
                  </tr>
                ))}
                {notes.length === 0 && (
                  <tr><td colSpan="12" className="text-center text-muted">No return notes yet. Raise one from a recorded invoice.</td></tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <Modal show={Boolean(viewing)} onHide={() => setViewing(null)} size="lg">
        <Modal.Header closeButton><Modal.Title>{viewing?.noteNumber}</Modal.Title></Modal.Header>
        <Modal.Body>
          {viewing && (
            <>
              <p className="mb-1"><strong>Vendor:</strong> {viewing.vendorName} &nbsp; <strong>Against invoice:</strong> {viewing.vendorInvoiceNumber} ({viewing.purchaseInvoiceNumber})</p>
              <p className="mb-1"><strong>Date:</strong> {formatDate(viewing.noteDate)} &nbsp; <strong>Reason:</strong> {PURCHASE_NOTE_REASONS[viewing.reason] || viewing.reason}</p>
              {viewing.vendorNoteNumber && <p className="mb-1"><strong>Vendor&apos;s credit note:</strong> {viewing.vendorNoteNumber} {formatDate(viewing.vendorNoteDate)}</p>}
              {viewing.status === 'CANCELLED' && <p className="text-danger mb-1"><strong>Cancelled:</strong> {viewing.cancelReason}</p>}
              <Table bordered size="sm" className="mt-2">
                <thead>
                  <tr><th>Line</th><th className="text-end">Qty</th><th className="text-end">Taxable</th><th className="text-end">CGST</th><th className="text-end">SGST</th><th className="text-end">IGST</th><th>Credit</th></tr>
                </thead>
                <tbody>
                  {(viewing.lines || []).map((l) => (
                    <tr key={l.id}>
                      <td>{l.description}</td><td className="text-end">{l.quantity ?? '-'}</td>
                      <td className="text-end">{money(l.taxableAmount)}</td><td className="text-end">{money(l.cgstAmount)}</td>
                      <td className="text-end">{money(l.sgstAmount)}</td><td className="text-end">{money(l.igstAmount)}</td>
                      <td>{l.itcEligible ? 'Given back' : 'Not claimed'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="text-end">Value {money(viewing.taxableAmount)} + GST {money(viewing.totalTaxAmount)} = <strong>{money(viewing.totalAmount)}</strong></div>
              {viewing.notes && <p className="mt-2"><strong>Remarks:</strong> {viewing.notes}</p>}
              {cancelling && (
                <Form.Group className="mt-3">
                  <Form.Label>Reason for cancelling</Form.Label>
                  <Form.Control value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
                </Form.Group>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {viewing?.status === 'ISSUED' && !cancelling && (
            (viewing.ginNumbers || []).length === 0
              ? <Button variant="outline-danger" onClick={() => setCancelling(true)}>Cancel note</Button>
              : <span className="text-muted small me-auto">Stock went out against this note, so it can&apos;t be cancelled.</span>
          )}
          {cancelling && (
            <>
              <Button variant="secondary" onClick={() => setCancelling(false)}>Keep it</Button>
              <Button variant="danger" onClick={cancel} disabled={!reason.trim()}>Cancel note</Button>
            </>
          )}
          {!cancelling && <Button variant="secondary" onClick={() => setViewing(null)}>Close</Button>}
        </Modal.Footer>
      </Modal>
      <GstRegistrationModal
        show={Boolean(ewayNote)}
        docType="PURCHASE_NOTE"
        docId={ewayNote?.id}
        label={ewayNote?.noteNumber}
        apiBase={ewayNote ? `/api/eway-movements/purchase-notes/${ewayNote.id}` : undefined}
        onHide={() => setEwayNote(null)}
        canCancel={hasAnyRole(user, ['ORG_ADMIN', 'PROC_MGR'])}
      />
    </div>
  );
}
